import React, { useState, useEffect, memo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Heart,
  Music,
  Play,
  Pause,
  SkipForward,
  SkipBack,
  ListMusic,
  Disc,
  Repeat,
  Shuffle,
  Volume2,
  VolumeX,
  X,
  Sparkles,
  Quote,
} from 'lucide-react';
import { SongTrack, CoupleSettings } from '../types';
import { musicPlayer, PlayerState, LoopMode, TimeState } from '../utils/musicPlayer';

interface RomanticMusicPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  playerState: PlayerState;
  playlist: SongTrack[];
  settings: CoupleSettings;
  onOpenAdminMusic?: () => void;
}

function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
}

// ---------------------------------------------------------------------------
// 1. ISOLATED SCRUBBER COMPONENT
// Subscribes directly to high-frequency audio time updates.
// Prevents the entire turntable deck & modal tree from re-rendering every 250ms!
// ---------------------------------------------------------------------------
const PlayerScrubber: React.FC = memo(() => {
  const [timeState, setTimeState] = useState<TimeState>(() => musicPlayer.getTimeState());

  useEffect(() => {
    const unsub = musicPlayer.subscribeTime((newTime) => {
      setTimeState(newTime);
    });
    return unsub;
  }, []);

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const targetSeconds = parseFloat(e.target.value);
    musicPlayer.seek(targetSeconds);
  };

  const progressPercent =
    timeState.duration > 0
      ? Math.min(100, Math.max(0, (timeState.currentTime / timeState.duration) * 100))
      : 0;

  return (
    <div className="space-y-1.5 px-1">
      {/* Scrubber Track Container */}
      <div className="relative flex items-center h-5 group cursor-pointer">
        {/* Base Unplayed Track */}
        <div className="absolute inset-x-0 h-1.5 bg-[#F0DDE4] rounded-full overflow-hidden pointer-events-none">
          {/* Played Progress Bar */}
          <div
            className="h-full bg-gradient-to-r from-[#E85D8E] to-[#E83E78] rounded-full shadow-[0_0_8px_rgba(232,93,142,0.6)] transition-[width] duration-75"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Thumb Pin at progress position */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#E83E78] shadow-[0_2px_8px_rgba(232,62,120,0.5)] border-2 border-white pointer-events-none transition-transform duration-75 group-hover:scale-125"
          style={{ left: `${progressPercent}%` }}
        />

        {/* Native range input for accessible touch & mouse scrubbing */}
        <input
          type="range"
          min="0"
          max={timeState.duration || 100}
          step="0.5"
          value={timeState.currentTime || 0}
          onChange={handleSeek}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          aria-label="Progreso de reproducción"
        />
      </div>

      {/* Time indicators */}
      <div className="flex items-center justify-between text-[11px] font-mono px-0.5 select-none">
        <span className="text-[#302B30] font-bold">{formatTime(timeState.currentTime)}</span>
        <span className="text-[#77717A] font-semibold">{formatTime(timeState.duration)}</span>
      </div>
    </div>
  );
});
PlayerScrubber.displayName = 'PlayerScrubber';

// ---------------------------------------------------------------------------
// 2. ISOLATED MEMOIZED TURNTABLE DECK
// Uses CSS repeating radial gradient for authentic vinyl grooves (0 extra DOM elements).
// Uses zero-filter GPU radial gradient aura instead of heavy blur-3xl/blur-xl.
// ---------------------------------------------------------------------------
interface TurntableDeckProps {
  isPlaying: boolean;
  partner1: string;
  partner2: string;
}

const TurntableDeck: React.FC<TurntableDeckProps> = memo(({ isPlaying, partner1, partner2 }) => {
  return (
    <div className="relative w-full max-w-[340px] sm:max-w-[370px] mx-auto bg-gradient-to-b from-[#FFFDFB] via-[#FFF4F7] to-[#F8F5F7] rounded-3xl p-4 sm:p-5 border border-[#F0DDE4] shadow-[0_10px_30px_rgba(232,93,142,0.08),inset_0_1px_1px_rgba(255,255,255,0.8)]">
      {/* Decorative Corner Screws */}
      <div className="absolute top-2.5 left-2.5 w-1.5 h-1.5 rounded-full bg-[#F0DDE4] border border-[#E85D8E]/30" />
      <div className="absolute top-2.5 right-2.5 w-1.5 h-1.5 rounded-full bg-[#F0DDE4] border border-[#E85D8E]/30" />
      <div className="absolute bottom-2.5 left-2.5 w-1.5 h-1.5 rounded-full bg-[#F0DDE4] border border-[#E85D8E]/30" />
      <div className="absolute bottom-2.5 right-2.5 w-1.5 h-1.5 rounded-full bg-[#F0DDE4] border border-[#E85D8E]/30" />

      {/* Turntable Platter Area */}
      <div className="relative flex items-center justify-center py-2">
        {/* Hardware-accelerated ambient glow without expensive blur filter */}
        <div
          className="absolute w-52 h-52 rounded-full pointer-events-none transition-opacity duration-700"
          style={{
            background: isPlaying
              ? 'radial-gradient(circle, rgba(232,93,142,0.2) 0%, rgba(255,244,247,0.1) 50%, transparent 72%)'
              : 'transparent',
            opacity: isPlaying ? 1 : 0,
          }}
        />

        {/* Recessed Platter Well */}
        <div className="relative w-52 h-52 sm:w-56 sm:h-56 rounded-full bg-stone-900 border-4 border-stone-800 shadow-[inset_0_4px_16px_rgba(0,0,0,0.6),0_4px_20px_rgba(232,93,142,0.15)] flex items-center justify-center overflow-hidden">
          {/* Realistic Vinyl Record Disc (Hardware-accelerated CSS pattern) */}
          <div
            className="relative w-48 h-48 sm:w-52 sm:h-52 rounded-full border border-stone-800 shadow-[0_4px_25px_rgba(0,0,0,0.6)] flex items-center justify-center transform-gpu"
            style={{
              background:
                'radial-gradient(circle, #0d0d0f 0%, #0d0d0f 22%, #191823 23%, #0d0d0f 24%), repeating-radial-gradient(circle at center, #0d0d0f 0, #0d0d0f 3px, #191822 4px, #0d0d0f 5px)',
              animationName: 'spin',
              animationDuration: '10s',
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite',
              animationPlayState: isPlaying ? 'running' : 'paused',
              willChange: 'transform',
            }}
          >
            {/* Vinyl Dynamic Light Reflection / Sheen */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-white/[0.08] to-transparent pointer-events-none transform rotate-45" />

            {/* Center Vinyl Label with Couple Seal */}
            <div className="relative w-18 h-18 sm:w-20 sm:h-20 rounded-full bg-gradient-to-tr from-[#E85D8E] via-[#E83E78] to-[#FF8DAF] shadow-[0_2px_12px_rgba(232,93,142,0.5)] border-2 border-white/80 flex flex-col items-center justify-center text-center p-1">
              <Heart className="w-4 h-4 text-white fill-white animate-pulse" />
              <span className="text-[8.5px] font-black uppercase tracking-wider text-white mt-0.5">
                {partner1.slice(0, 1)} & {partner2.slice(0, 1)}
              </span>
              <span className="text-[7.5px] text-pink-100 font-bold tracking-tight">
                AMOR
              </span>

              {/* Center Brass Spindle Hole */}
              <div className="w-2.5 h-2.5 rounded-full bg-stone-900 border border-amber-300 shadow-inner mt-0.5" />
            </div>
          </div>
        </div>

        {/* Authentic Tonearm Assembly */}
        <div className="absolute top-1 right-2 sm:right-3 pointer-events-none z-20">
          <div className="relative w-8 h-8 rounded-full bg-gradient-to-b from-stone-200 via-stone-300 to-stone-400 border border-[#F0DDE4] shadow-md flex items-center justify-center">
            <div className="w-5 h-5 rounded-full bg-gradient-to-tr from-rose-200 to-amber-100 border border-rose-300 shadow-inner" />
            <div className="w-2 h-2 rounded-full bg-stone-600" />

            <div
              className="absolute top-3 left-3 w-1.5 transition-transform duration-500 ease-out origin-[top_left]"
              style={{
                transform: isPlaying ? 'rotate(24deg)' : 'rotate(-2deg)',
              }}
            >
              <div className="w-3.5 h-4 bg-gradient-to-r from-stone-300 via-rose-200 to-stone-400 rounded-xs -mt-5 -ml-1 border border-stone-300 shadow-xs" />
              <div className="w-1 h-28 sm:h-32 bg-gradient-to-b from-stone-300 via-stone-200 to-stone-400 rounded-full shadow-xs ml-0.25" />
              <div className="w-3 h-5 bg-gradient-to-b from-stone-700 to-stone-900 border border-amber-300 rounded-xs shadow-xs -ml-1 transform -rotate-15 flex flex-col items-center justify-end pb-0.5">
                <div
                  className={`w-1 h-1.5 rounded-full ${
                    isPlaying ? 'bg-[#E83E78] shadow-[0_0_6px_#E83E78]' : 'bg-stone-500'
                  }`}
                />
              </div>
            </div>
          </div>

          <div className="absolute top-24 right-0 w-3 h-3 rounded-full bg-stone-300 border border-stone-400 shadow-inner" />
        </div>
      </div>

      {/* Turntable Status Footer */}
      <div className="flex items-center justify-between pt-1 px-1 text-[10px] text-[#77717A]">
        <span className="flex items-center gap-1.5">
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              isPlaying ? 'bg-emerald-500 shadow-[0_0_6px_#10b981] animate-pulse' : 'bg-stone-400'
            }`}
          />
          <span className="font-mono font-semibold">{isPlaying ? '33 RPM • REPRODUCIENDO' : 'EN PAUSA'}</span>
        </span>
        <span className="font-semibold text-[#E83E78] font-mono">HI-FI AUDIO</span>
      </div>
    </div>
  );
});
TurntableDeck.displayName = 'TurntableDeck';

// ---------------------------------------------------------------------------
// 3. ISOLATED MEMOIZED PLAYLIST VIEW
// ---------------------------------------------------------------------------
interface PlaylistViewProps {
  playlist: SongTrack[];
  currentTrackId?: string;
  isPlaying: boolean;
  partner1: string;
  partner2: string;
}

const PlaylistView: React.FC<PlaylistViewProps> = memo(
  ({ playlist, currentTrackId, isPlaying, partner1, partner2 }) => {
    return (
      <div className="relative z-10 pt-4 space-y-3">
        <div className="flex items-center justify-between px-1">
          <p className="text-xs text-[#77717A] font-semibold">
            {playlist.length} {playlist.length === 1 ? 'canción dedicada' : 'canciones dedicadas'}
          </p>
        </div>

        {playlist.length === 0 ? (
          <div className="p-8 text-center bg-[#FFF4F7] rounded-3xl border border-[#F0DDE4] space-y-2">
            <Music className="w-10 h-10 text-[#E85D8E]/40 mx-auto" />
            <p className="text-sm font-semibold text-[#302B30]">No hay canciones en la lista aún</p>
            <p className="text-xs text-[#77717A]">
              Agrega canciones románticas desde el panel de administración.
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[58vh] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-pink-200">
            {playlist.map((track, idx) => {
              const isCurrent = currentTrackId === track.id;
              return (
                <div
                  key={track.id || idx}
                  onClick={() => musicPlayer.playTrackById(track.id)}
                  className={`p-3 rounded-2xl flex items-center justify-between gap-3 transition-all cursor-pointer ${
                    isCurrent
                      ? 'bg-gradient-to-r from-[#FFF4F7] to-[#FFE8F0] border-2 border-[#E85D8E] text-[#302B30] shadow-sm'
                      : 'bg-white hover:bg-[#FFF4F7] border border-[#F0DDE4] text-[#302B30]'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold shrink-0 ${
                        isCurrent
                          ? 'bg-gradient-to-tr from-[#E85D8E] to-[#E83E78] text-white font-black shadow-xs'
                          : 'bg-[#FFF4F7] text-[#77717A] border border-[#F0DDE4]'
                      }`}
                    >
                      {isCurrent && isPlaying ? (
                        <span className="flex items-end gap-0.5 h-3">
                          <span className="w-0.5 bg-white h-full animate-bounce" />
                          <span className="w-0.5 bg-white h-2/3 animate-bounce delay-75" />
                          <span className="w-0.5 bg-white h-4/5 animate-bounce delay-150" />
                        </span>
                      ) : (
                        idx + 1
                      )}
                    </div>

                    <div className="min-w-0">
                      <p
                        className={`text-sm font-bold truncate ${
                          isCurrent ? 'text-[#E83E78]' : 'text-[#302B30]'
                        }`}
                      >
                        {track.title}
                      </p>
                      <p className="text-[11px] text-[#77717A] truncate">
                        {track.artist || `${partner1} & ${partner2}`}
                      </p>
                      {track.uploadedBy && (
                        <span className="inline-block mt-0.5 text-[9px] font-semibold text-[#E85D8E]">
                          {track.uploadedBy === 'novio'
                            ? `💙 Por ${partner1}`
                            : `💖 Por ${partner2}`}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5 shrink-0 text-xs font-mono text-[#77717A]">
                    {track.duration && <span>{track.duration}</span>}
                    {isCurrent && (
                      <span className="w-2 h-2 rounded-full bg-[#E83E78] shadow-[0_0_8px_rgba(232,62,120,0.8)] animate-ping" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);
PlaylistView.displayName = 'PlaylistView';

// ---------------------------------------------------------------------------
// 4. MAIN HIGH-PERFORMANCE ROMANTIC MUSIC PLAYER MODAL
// ---------------------------------------------------------------------------
export const RomanticMusicPlayerModal: React.FC<RomanticMusicPlayerModalProps> = memo(
  ({ isOpen, onClose, playerState, playlist, settings }) => {
    const [activeTab, setActiveTab] = useState<'player' | 'playlist'>('player');

    const currentTrack = playlist.length > 0 ? playerState.currentTrack : null;
    const isPlaying = playerState.isPlaying;

    // Toggle Loop
    const handleToggleLoop = () => {
      const modes: LoopMode[] = ['all', 'one', 'none'];
      const currentIdx = modes.indexOf(playerState.loopMode);
      const nextMode = modes[(currentIdx + 1) % modes.length];
      musicPlayer.setLoopMode(nextMode);
    };

    // Toggle Shuffle
    const handleToggleShuffle = () => {
      musicPlayer.toggleShuffle();
    };

    // Volume slider handler
    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const vol = parseFloat(e.target.value);
      musicPlayer.setVolume(vol);
    };

    const partner1 = settings.partner1Name || 'Jackson';
    const partner2 = settings.partner2Name || 'Laura';

    return (
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 font-['Plus_Jakarta_Sans',sans-serif]">
            {/* Backdrop: Ultra-fast GPU composite with backdrop-blur-md */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={onClose}
              className="fixed inset-0 bg-slate-900/35 backdrop-blur-md cursor-pointer transform-gpu will-change-opacity"
            />

            {/* Player Sheet Container: Smooth GPU-accelerated cubic-bezier ease */}
            <motion.div
              initial={{ y: '100%', opacity: 0.8 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full sm:max-w-md md:max-w-lg bg-white text-[#302B30] rounded-t-[32px] sm:rounded-[32px] border-t sm:border border-[#F0DDE4] shadow-[0_-10px_35px_rgba(232,93,142,0.18)] p-5 sm:p-6 max-h-[92vh] overflow-y-auto z-10 scrollbar-none transform-gpu will-change-transform"
              id="romantic-music-player-modal"
            >
              {/* Ambient Warm Aura */}
              <div
                className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full"
                style={{
                  background:
                    'radial-gradient(circle, rgba(232,93,142,0.12) 0%, rgba(255,244,247,0.4) 45%, transparent 70%)',
                }}
              />

              {/* Mobile Drag Indicator */}
              <div className="w-12 h-1.5 bg-[#F0DDE4] rounded-full mx-auto mb-3 sm:hidden" />

              {/* HEADER: TITLE + TABS + CLOSE */}
              <div className="relative z-10 flex items-center justify-between pb-3 border-b border-[#F0DDE4]">
                {/* Segmented View Switcher */}
                <div className="inline-flex items-center p-1 rounded-2xl bg-[#FFF4F7] border border-[#F0DDE4] text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => setActiveTab('player')}
                    className={`px-3.5 py-1.5 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer ${
                      activeTab === 'player'
                        ? 'bg-gradient-to-r from-[#E85D8E] to-[#E83E78] text-white font-bold shadow-sm shadow-pink-500/25'
                        : 'text-[#77717A] hover:text-[#302B30]'
                    }`}
                  >
                    <Disc className={`w-3.5 h-3.5 ${isPlaying ? 'animate-spin' : ''}`} />
                    <span>Reproductor</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('playlist')}
                    className={`px-3.5 py-1.5 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer ${
                      activeTab === 'playlist'
                        ? 'bg-gradient-to-r from-[#E85D8E] to-[#E83E78] text-white font-bold shadow-sm shadow-pink-500/25'
                        : 'text-[#77717A] hover:text-[#302B30]'
                    }`}
                  >
                    <ListMusic className="w-3.5 h-3.5" />
                    <span>Canciones</span>
                    <span className="ml-0.5 px-1.5 py-0.2 rounded-full bg-[#E85D8E]/15 text-[#E83E78] font-bold text-[10px]">
                      {playlist.length}
                    </span>
                  </button>
                </div>

                {/* Close Button */}
                <button
                  type="button"
                  onClick={onClose}
                  className="w-8 h-8 rounded-full bg-[#FFF4F7] hover:bg-[#FFE8F0] text-[#77717A] hover:text-[#302B30] flex items-center justify-center transition-all cursor-pointer border border-[#F0DDE4]"
                  title="Cerrar reproductor"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* VIEW TAB 1: PLAYER SHOWCASE */}
              {activeTab === 'player' ? (
                <div className="relative z-10 pt-3 space-y-4">
                  {/* Turntable Deck (Memoized) */}
                  <TurntableDeck
                    isPlaying={isPlaying}
                    partner1={partner1}
                    partner2={partner2}
                  />

                  {/* Song Title & Artist */}
                  <div className="text-center space-y-1 px-2">
                    <h4 className="font-['Playfair_Display',serif] text-lg sm:text-xl font-bold text-[#302B30] tracking-tight truncate">
                      {currentTrack
                        ? currentTrack.title
                        : playlist.length === 0
                        ? 'Sin música agregada'
                        : 'Selecciona una canción'}
                    </h4>

                    <p className="text-xs text-[#77717A] font-semibold truncate">
                      {currentTrack
                        ? currentTrack.artist || `${partner1} & ${partner2}`
                        : 'Lista de reproducción vacía'}
                    </p>

                    {/* Dedication Tag */}
                    {currentTrack?.uploadedBy && (
                      <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-[#FFF4F7] border border-[#F0DDE4] text-[#E83E78] text-[11px] font-bold mt-1">
                        <Sparkles className="w-3 h-3 text-[#E83E78]" />
                        <span>
                          {currentTrack.uploadedBy === 'novio'
                            ? `Dedicada con amor por ${partner1}`
                            : `Dedicada con amor por ${partner2}`}
                        </span>
                      </div>
                    )}

                    {/* Dedication Quote Note */}
                    {currentTrack?.notes && (
                      <div className="mt-2 p-2.5 rounded-2xl bg-[#FFF4F7] border border-[#F0DDE4] max-w-sm mx-auto flex items-start gap-2 text-left">
                        <Quote className="w-3.5 h-3.5 text-[#E85D8E] shrink-0 mt-0.5" />
                        <p className="text-[11px] text-[#302B30] italic leading-relaxed line-clamp-2">
                          "{currentTrack.notes}"
                        </p>
                      </div>
                    )}
                  </div>

                  {/* High-Performance Scrubber (Direct Time Updates) */}
                  <PlayerScrubber />

                  {/* PRIMARY PLAYBACK CONTROLS */}
                  <div className="flex items-center justify-between px-3 pt-1">
                    {/* Shuffle Button */}
                    <button
                      type="button"
                      onClick={handleToggleShuffle}
                      className={`p-2 rounded-xl transition-all cursor-pointer ${
                        playerState.isShuffle
                          ? 'bg-[#FFF4F7] text-[#E83E78] border border-[#F0DDE4] shadow-xs'
                          : 'text-[#77717A] hover:text-[#302B30]'
                      }`}
                      title={playerState.isShuffle ? 'Desactivar aleatorio' : 'Activar aleatorio'}
                    >
                      <Shuffle className="w-4 h-4" />
                    </button>

                    {/* Previous Track */}
                    <button
                      type="button"
                      onClick={() => musicPlayer.previousTrack()}
                      className="p-3 rounded-full bg-[#FFF4F7] hover:bg-[#FFE8F0] text-[#302B30] transition-all active:scale-90 cursor-pointer border border-[#F0DDE4] shadow-xs"
                      title="Canción anterior (Devolver)"
                      aria-label="Canción anterior"
                    >
                      <SkipBack className="w-5 h-5 fill-current text-[#E83E78]" />
                    </button>

                    {/* Hero Play/Pause Button */}
                    <button
                      type="button"
                      onClick={() => musicPlayer.togglePlay()}
                      className={`w-14 h-14 rounded-full flex items-center justify-center transition-all cursor-pointer shadow-lg active:scale-95 ${
                        isPlaying
                          ? 'bg-gradient-to-tr from-[#E85D8E] to-[#E83E78] text-white shadow-[0_4px_20px_rgba(232,93,142,0.4)] scale-105'
                          : 'bg-gradient-to-tr from-[#E85D8E] to-[#E83E78] text-white hover:scale-105 shadow-[0_4px_20px_rgba(232,93,142,0.3)]'
                      }`}
                      title={isPlaying ? 'Pausar' : 'Reproducir'}
                      aria-label={isPlaying ? 'Pausar' : 'Reproducir'}
                    >
                      {isPlaying ? (
                        <Pause className="w-6 h-6 fill-current" />
                      ) : (
                        <Play className="w-6 h-6 fill-current ml-0.5" />
                      )}
                    </button>

                    {/* Next Track */}
                    <button
                      type="button"
                      onClick={() => musicPlayer.nextTrack()}
                      className="p-3 rounded-full bg-[#FFF4F7] hover:bg-[#FFE8F0] text-[#302B30] transition-all active:scale-90 cursor-pointer border border-[#F0DDE4] shadow-xs"
                      title="Siguiente canción (Pasar)"
                      aria-label="Siguiente canción"
                    >
                      <SkipForward className="w-5 h-5 fill-current text-[#E83E78]" />
                    </button>

                    {/* Repeat Loop Mode */}
                    <button
                      type="button"
                      onClick={handleToggleLoop}
                      className={`p-2 rounded-xl transition-all relative cursor-pointer ${
                        playerState.loopMode !== 'none'
                          ? 'bg-[#FFF4F7] text-[#E83E78] border border-[#F0DDE4] shadow-xs'
                          : 'text-[#77717A] hover:text-[#302B30]'
                      }`}
                      title={`Modo de repetición: ${
                        playerState.loopMode === 'all'
                          ? 'Toda la lista'
                          : playerState.loopMode === 'one'
                          ? 'Canción actual'
                          : 'Desactivado'
                      }`}
                    >
                      <Repeat className="w-4 h-4" />
                      {playerState.loopMode === 'one' && (
                        <span className="absolute -top-1 -right-1 text-[8px] bg-[#E83E78] text-white font-black rounded-full px-1">
                          1
                        </span>
                      )}
                    </button>
                  </div>

                  {/* VOLUME SLIDER */}
                  <div className="flex items-center gap-3 px-2 pt-2 border-t border-[#F0DDE4]">
                    <button
                      type="button"
                      onClick={() => musicPlayer.toggleMute()}
                      className="text-[#77717A] hover:text-[#302B30] transition-colors cursor-pointer"
                      title={playerState.isMuted ? 'Activar sonido' : 'Silenciar'}
                    >
                      {playerState.isMuted || playerState.volume === 0 ? (
                        <VolumeX className="w-4 h-4 text-rose-500" />
                      ) : (
                        <Volume2 className="w-4 h-4 text-[#E83E78]" />
                      )}
                    </button>

                    {/* Volume Slider with active track fill */}
                    <div className="relative flex-1 flex items-center h-4 group cursor-pointer">
                      <div className="absolute inset-x-0 h-1.5 bg-[#F0DDE4] rounded-full overflow-hidden pointer-events-none">
                        <div
                          className="h-full bg-gradient-to-r from-[#E85D8E] to-[#E83E78] rounded-full"
                          style={{
                            width: `${(playerState.isMuted ? 0 : playerState.volume) * 100}%`,
                          }}
                        />
                      </div>
                      <div
                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-[#E83E78] shadow-[0_2px_6px_rgba(232,62,120,0.5)] border border-white pointer-events-none transition-transform group-hover:scale-125"
                        style={{
                          left: `${(playerState.isMuted ? 0 : playerState.volume) * 100}%`,
                        }}
                      />
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.02"
                        value={playerState.isMuted ? 0 : playerState.volume}
                        onChange={handleVolumeChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        aria-label="Control de volumen"
                      />
                    </div>

                    <span className="text-[10px] text-[#77717A] font-mono shrink-0 w-8 text-right font-semibold">
                      {Math.round((playerState.isMuted ? 0 : playerState.volume) * 100)}%
                    </span>
                  </div>
                </div>
              ) : (
                /* VIEW TAB 2: COMPLETE INTERACTIVE PLAYLIST */
                <PlaylistView
                  playlist={playlist}
                  currentTrackId={currentTrack?.id}
                  isPlaying={isPlaying}
                  partner1={partner1}
                  partner2={partner2}
                />
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    );
  }
);
RomanticMusicPlayerModal.displayName = 'RomanticMusicPlayerModal';
